/**
 * Fuzzy matching for the command palette, in the style of VS Code.
 *
 * The query is split on whitespace and every token must match, so "pla 2"
 * matches "Placeholder 2". A token matches one of two ways:
 *
 *  - as a run of characters, e.g. "adv" in "Search advertiser";
 *  - as initials, e.g. "sm" for "Switch module".
 *
 * Spaces in the target are not required either: "adnuntius" also matches
 * "Ad Nuntius", the way the query's own spaces already let "ad nuntius" match
 * "Adnuntius".
 *
 * Both are deliberately stricter than matching the token as a subsequence of
 * the whole string: over a long label, a subsequence match accepts nearly any
 * combination of letters, so nothing ever drops out of the list.
 */

const WORD_BOUNDARY = ' -_/.:,'

function isWordStart(target: string, index: number): boolean {
  return index === 0 || WORD_BOUNDARY.includes(target[index - 1])
}

/** Scores a token appearing as a run of characters, 0 when it does not. */
function scoreRun(target: string, token: string): number {
  const index = target.indexOf(token)
  if (index === -1) return 0

  // A word start beats a match buried inside a longer word, and an earlier
  // match beats a later one.
  let score = 10 + token.length * 2
  if (isWordStart(target, index)) score += 10
  if (index === 0) score += 5
  return score + Math.max(0, 10 - index)
}

/**
 * Scores a token as a run of characters in the target with its spaces taken
 * out, so a run may cross them. Ranked below a run found with the spaces in.
 */
function scoreCompactRun(target: string, token: string): number {
  const index = target.replace(/\s+/g, '').indexOf(token)
  if (index === -1) return 0
  return 5 + token.length * 2
}

/** Scores a token against the target's initials, e.g. "sm" for "switch module". */
function scoreInitials(target: string, token: string): number {
  const initials = target
    .split(/[\s\-_/.:,]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')

  // The initials have to appear in order, but need not be adjacent.
  let from = 0
  for (const char of token) {
    const index = initials.indexOf(char, from)
    if (index === -1) return 0
    from = index + 1
  }
  return 8 + token.length
}

/** Returns 0 when the query does not match, or a positive relevance score. */
export function fuzzyScore(target: string, query: string): number {
  const haystack = target.toLowerCase()
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return 1

  let total = 0
  for (const token of tokens) {
    const score = Math.max(
      scoreRun(haystack, token),
      scoreCompactRun(haystack, token),
      scoreInitials(haystack, token)
    )
    if (score === 0) return 0
    total += score
  }

  // Prefer shorter targets when the scores are otherwise equal.
  return total + Math.max(0, 20 - haystack.length)
}
